import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PurchaseAdd from './PurchaseAdd';
import SupplierDues from './SupplierDues';
import PageHeader from '../common/PageHeader';
import SearchField from '../common/SearchField';
import NoDataFound from '../Utils/NoDataFound';
import { useDeletePurchaseMutation, useGetPurchasePageMutation, useLazyGetPurchaseByIdQuery } from '../../api/purchase/PurchaseApi';
import { MasterRow } from '../../types/master';
import { Purchase } from '../../types/purchase';
import { shortDate } from '../../utils/dates';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';
import { invoiceDate } from '../../utils/dates';

const PurchaseView = () => {
  const [view, setView] = useState<'LIST' | 'CREATE'>('LIST');
  const [tab, setTab] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [deleteTarget, setDeleteTarget] = useState<MasterRow | null>(null);
  const [detail, setDetail] = useState<Purchase | null>(null);

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { openSnackbar } = useSnackbar();
  const [getPurchasePage] = useGetPurchasePageMutation();
  const [getPurchaseById] = useLazyGetPurchaseByIdQuery();
  const [deletePurchase, { isLoading: deleting }] = useDeletePurchaseMutation();

  const load = (search: string, page: number, size: number) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await getPurchasePage({ searchValue: search, page, size }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  useEffect(() => {
    if (view === 'LIST') load(searchValue, paginationModel.page, paginationModel.pageSize);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, paginationModel.page, paginationModel.pageSize, view]);

  const openDetail = async (id: number) => {
    try {
      const purchase = await getPurchaseById(id).unwrap();
      setDetail(purchase);
    } catch {
      openSnackbar('Could not open the purchase', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deletePurchase(Number(deleteTarget.id)).unwrap();
      openSnackbar('Purchase deleted and stock reversed', 'success');
      load(searchValue, paginationModel.page, paginationModel.pageSize);
    } catch (error: any) {
      // Refused once any phone from this invoice has been sold.
      openSnackbar(error?.data?.message || 'Could not delete the purchase', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'invoiceNo', headerName: 'Invoice No', flex: 1, minWidth: 130, align: 'center', headerAlign: 'center' },
    {
      field: 'invoiceDate',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => invoiceDate(value as string),
    },
    { field: 'supplierName', headerName: 'Supplier', flex: 1, minWidth: 160, align: 'center', headerAlign: 'center' },
    { field: 'taxAmount', headerName: 'GST', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    { field: 'totalAmount', headerName: 'Total', flex: 1, minWidth: 120, align: 'center', headerAlign: 'center' },
    {
      field: 'Action',
      headerName: 'Action',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (row) => (
        <>
          <IconButton size="small" onClick={() => openDetail(Number(row.row.id))} aria-label="View">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {can('PURCHASE', 'delete') && (
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.row)} aria-label="Delete">
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  if (view === 'CREATE') {
    return <PurchaseAdd onDone={() => setView('LIST')} />;
  }

  return (
    <Box>
      <PageHeader title="Purchase" subtitle="Supplier invoices. Every line becomes a batch.">
        {can('PURCHASE', 'create') && (
          <Button variant="contained" size="small" startIcon={<AddShoppingCartIcon />} onClick={() => setView('CREATE')}>
            Create
          </Button>
        )}
      </PageHeader>

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Invoices" />
        <Tab label="Supplier Dues" />
      </Tabs>

      {tab === 1 && <SupplierDues />}

      {tab === 0 && (
        <>
      <Box sx={{ mb: 1 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Invoice number or supplier..." />
      </Box>

      {rows.length === 0 && !loading ? (
        <NoDataFound onCreateClick={() => setView('CREATE')} buttonName="Create" message="No purchase entries yet" />
      ) : (
        <DataGrid
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          sx={{ height: '70vh' }}
        />
      )}

        </>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Invoice {detail?.invoiceNo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {detail?.supplierName} &middot; {detail?.invoiceDate}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail?.items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {item.productName}
                    {(item.brandName || item.modelName) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[item.brandName, item.modelName].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    {item.imeis?.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.imeis.map((entry) => (
                          <Chip key={entry.imeiNo} label={entry.imeiNo} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.batchNo ? (
                      <Chip size="small" variant="outlined" label={`${item.batchNo} · ${shortDate(detail?.invoiceDate)}`} />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">{item.commonPrice === false ? 'Per piece' : item.purchasePrice}</TableCell>
                  <TableCell align="right">{item.totalAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="flex-end" gap={4} sx={{ mt: 2 }}>
            <Typography variant="body2">GST: {detail?.taxAmount}</Typography>
            <Typography variant="body2" fontWeight={700}>
              Total: {detail?.totalAmount}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setDetail(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberRoundedIcon color="warning" />
          <Typography fontWeight={700} fontSize="1rem">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Deleting invoice{' '}
            <Box component="span" fontWeight={700} color="text.primary">
              {String(deleteTarget?.invoiceNo ?? '')}
            </Box>{' '}
            also removes the phones it brought into stock. It is refused if any of them are already sold.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setDeleteTarget(null)}>
            No, Cancel
          </Button>
          <LoadingButton variant="contained" color="error" size="small" loading={deleting} onClick={confirmDelete}>
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseView;
